import { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Page, Header } from 'components';
import { Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  boaImportFile,
  boaGetLastUpdate,
  boaGenerateResult,
  helixFetchData,
  helixExportResult,
  helixGetStatus,
  helixGetQueue,
} from '../Actions/BackOnAirThunks';
import './BackOnAir.css';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  container: {
    marginTop: theme.spacing(3),
  },
}));

// ─── ICONS ────────────────────────────────────────────────────────────────────
const Icon = {
  Upload: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  ),
  Zap: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  Download: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  User: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Time: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Hash: ({ size = 13, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/>
      <line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/>
    </svg>
  ),
  X: ({ size = 16, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Csv: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#0e7490" strokeWidth="1.5"/>
      <polyline points="13 2 13 9 20 9" stroke="#0e7490" strokeWidth="1.5"/>
      <line x1="8" y1="13" x2="16" y2="13" stroke="#0e7490" strokeWidth="1.5"/>
      <line x1="8" y1="17" x2="16" y2="17" stroke="#0e7490" strokeWidth="1.5"/>
    </svg>
  ),
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatTimestamp = (value) => {
  if (!value || value === '0001-01-01T00:00:00' || value === '0001-01-01') return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
  } catch { return ''; }
};

// ─── SPINNER ──────────────────────────────────────────────────────────────────
function Spinner({ dark = false }) {
  return <span className={`boa-spinner${dark ? ' boa-spinner-dark' : ''}`} />;
}

// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
function ActionButton({ iconColor, iconBg, IconComp, label, description, onClick, loading }) {
  return (
    <button className="boa-action-btn" onClick={onClick} disabled={loading}>
      <div className="boa-action-icon" style={{ background: iconBg }}>
        {loading ? <Spinner dark /> : <IconComp size={16} color={iconColor} />}
      </div>
      <span className="boa-action-btn-label">{label}</span>
      {description && <span className="boa-action-btn-desc">{description}</span>}
    </button>
  );
}

// ─── LAST UPDATE ─────────────────────────────────────────────────────────────
function LastUpdate({ user, timestamp }) {
  const formatted = formatTimestamp(timestamp);
  const hasData   = user || formatted;
  const rows = [
    ...(user      ? [{ Icon: Icon.User, text: user }]      : []),
    ...(formatted ? [{ Icon: Icon.Time, text: formatted }]  : []),
  ];

  return (
    <div>
      <p className="boa-last-update-label">Last update</p>
      {hasData ? rows.map(({ Icon: Ic, text }, i) => (
        <div key={i} className="boa-last-update-row">
          <span className="boa-last-update-row-icon"><Ic /></span>
          <span className="boa-last-update-row-text">{text}</span>
        </div>
      )) : (
        <p className="boa-no-data">No data available</p>
      )}
    </div>
  );
}

// ─── HELIX STATUS ─────────────────────────────────────────────────────────────
function HelixStatus({ status }) {
  // status: null | 'loading' | 'done'
  if (!status) return null;

  const isDone = status === 'done';

  return (
    <div className="boa-helix-status">
      <div className={`boa-helix-track ${isDone ? 'done' : 'loading'}`}>
        {isDone ? (
          <>
            <div className="boa-helix-ring boa-helix-ring-done" />
            <svg className="boa-helix-check" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </>
        ) : (
          <>
            <div className="boa-helix-ring boa-helix-ring-spin" />
            <div className="boa-helix-pulse" />
          </>
        )}
      </div>
      <span className={`boa-helix-label ${isDone ? 'done' : 'loading'}`}>
        {isDone ? 'Done' : 'Running…'}
      </span>
    </div>
  );
}

// ─── MESSAGE MODAL ────────────────────────────────────────────────────────────
function MessageModal({ message, onClose }) {
  return (
    <div className="boa-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="boa-modal" style={{ maxWidth: 560 }}>
        <div className="boa-modal-header">
          <p className="boa-modal-title">Message</p>
          <button className="boa-close-btn" onClick={onClose}><Icon.X /></button>
        </div>
        <div className="boa-modal-body">
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'Inter', sans-serif" }}>
            {message}
          </p>
        </div>
        <div className="boa-modal-footer">
          <button className="boa-btn boa-btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── UPLOAD MODAL ─────────────────────────────────────────────────────────────
function UploadModal({ title, subtitle, onClose, onConfirm, loading }) {
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError]       = useState('');
  const inputRef                = useRef(null);

  const MAX_SIZE_MB    = 100;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const isValid = (f) => f.name.endsWith('.csv');

  const applyFile = (f) => {
    if (!f) return;
    if (!isValid(f)) { setError('Only .csv files are accepted.'); return; }
    if (f.size > MAX_SIZE_BYTES) { setError(`File size exceeds the ${MAX_SIZE_MB}MB limit.`); return; }
    setError(''); setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false); applyFile(e.dataTransfer.files[0]);
  }, []);

  return (
    <div className="boa-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="boa-modal">
        <div className="boa-modal-header">
          <div>
            <p className="boa-modal-title">{title}</p>
            {subtitle && <p className="boa-modal-subtitle">{subtitle}</p>}
          </div>
          <button className="boa-close-btn" onClick={onClose}><Icon.X /></button>
        </div>

        <div className="boa-modal-body">
          <div
            className={`boa-dropzone${dragOver ? ' drag-over' : ''}${file ? ' has-file' : ''}`}
            onClick={() => !file && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="boa-file-info">
                <Icon.Csv />
                <p className="boa-file-name">{file.name}</p>
                <p className="boa-file-size">{(file.size / 1024).toFixed(1)} KB</p>
                <button className="boa-remove-btn" onClick={(e) => { e.stopPropagation(); setFile(null); setError(''); }}>
                  Remove
                </button>
              </div>
            ) : (
              <>
                <Icon.Upload size={20} color={dragOver ? '#3b82f6' : '#94a3b8'} />
                <p className="boa-dropzone-title">{dragOver ? 'Drop to upload' : 'Drag & drop or click to browse'}</p>
                <p className="boa-dropzone-sub">.csv — max 100MB</p>
              </>
            )}
          </div>
          <input
            ref={inputRef} type="file"
            accept=".csv,text/csv"
            onChange={(e) => applyFile(e.target.files[0])}
            style={{ display: 'none' }}
          />
          {error && <p className="boa-error-msg">{error}</p>}
        </div>

        <div className="boa-modal-footer">
          <button className="boa-btn boa-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="boa-btn boa-btn-primary"
            onClick={() => file && onConfirm(file)}
            disabled={!file || loading}
          >
            {loading ? <><Spinner /> Uploading...</> : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
function BackOnAir() {
  const classes    = useStyles();
  const dispatch   = useDispatch();
  const loading    = useSelector((state) => state.backOnAirReducer.loading);
  const backOnAir  = useSelector((state) => state.backOnAirReducer.backOnAir);
  const helixQueue = useSelector((state) => state.backOnAirReducer.helixQueue);
  const userId     = useSelector((state) => state.session.user?.id);

  const [modal, setModal]                 = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [helixStatus, setHelixStatus] = useState(null); // null | 'loading' | 'done'
  const [helixInfo, setHelixInfo]     = useState(null); // { insertDate, insertUser, pending }
  const pollingRef                    = useRef(null);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const applyHelixResponse = (data) => {
    if (!data) return;
    const status = data.status ?? data.Status;
    setHelixInfo({
      insertDate: data.insertDate ?? data.InsertDate ?? null,
      insertUser: data.insertUser ?? data.InsertUser ?? null,
      pending:    data.pending    ?? data.Pending    ?? null,
    });
    if (status === 'DONE') {
      setHelixStatus('done');
    } else {
      setHelixStatus('loading');
    }
    return status;
  };

  const startPolling = () => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      dispatch(helixGetStatus()).then(applyHelixResponse);
      dispatch(helixGetQueue());
    }, 5000);
  };

  useEffect(() => {
    dispatch(boaGetLastUpdate());
    dispatch(helixGetStatus()).then((data) => {
      applyHelixResponse(data);
    });
    dispatch(helixGetQueue());
    startPolling();
    return () => stopPolling();
  }, []);

  const handleImport = (file) => {
    dispatch(boaImportFile(userId, file))
      .then(() => dispatch(boaGetLastUpdate()));
    setModal(null);
  };

  const handleHelixFetch = () => {
    setHelixStatus('loading');
    startPolling();
    dispatch(helixFetchData(userId))
      .then(() => dispatch(helixGetQueue()));
  };

  const handleHelixExport = () => {
    dispatch(helixExportResult(userId));
  };

  return (
    <Page className={classes.root} title="Back On Air">
      <Header module="NASP" view="Back On Air" />
      <Card className={classes.container}>
        <CardContent style={{ padding: 0 }}>
          <div className="boa-root">

            {selectedMessage && (
              <MessageModal
                message={selectedMessage}
                onClose={() => setSelectedMessage(null)}
              />
            )}
            {modal === 'import' && (
              <UploadModal
                title="Import Back On Air file"
                subtitle="Only .csv files accepted — max 100MB"
                onClose={() => setModal(null)}
                onConfirm={handleImport}
                loading={loading.importFile}
              />
            )}

            <div style={{ maxWidth: 1300, margin: '0 auto', padding: '32px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* ── TOP ROW: two cards side by side ─────────────────────── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

                {/* ── Back On Air Process ──────────────────────────────────── */}
                <div className="boa-card">
                  <div className="boa-card-header">
                    <span className="boa-card-title">Back On Air Process</span>
                    <span className="boa-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>Active</span>
                  </div>
                  <div className="boa-card-body">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 220px', gap: '20px', alignItems: 'start' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <ActionButton
                            iconColor="#1d4ed8" iconBg="#eff6ff" IconComp={Icon.Upload}
                            label="Import file"
                            description="Upload a new .csv file"
                            onClick={() => setModal('import')}
                            loading={loading.importFile}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <ActionButton
                            iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Zap}
                            label="Generate result"
                            description="Process current data"
                            onClick={() => dispatch(boaGenerateResult(userId))}
                            loading={loading.generateResult}
                          />
                        </div>
                      </div>

                      <div style={{ background: '#f1f5f9', width: 1, alignSelf: 'stretch' }} />

                      <LastUpdate
                        user={backOnAir.lastUpdate.user}
                        timestamp={backOnAir.lastUpdate.timestamp}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Back On Air Helix ────────────────────────────────────── */}
                <div className="boa-card">
                  <div className="boa-card-header">
                    <span className="boa-card-title">Back On Air Helix</span>
                    <span className="boa-badge" style={{ background: '#faf5ff', color: '#7c3aed' }}>Helix</span>
                  </div>
                  <div className="boa-card-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                        <ActionButton
                          iconColor="#7c3aed" iconBg="#faf5ff" IconComp={Icon.Zap}
                          label="Fetch Helix data"
                          description="Pull latest data from Helix"
                          onClick={handleHelixFetch}
                          loading={loading.helixFetch || helixStatus === 'loading'}
                        />
                        <ActionButton
                          iconColor="#0f766e" iconBg="#f0fdfa" IconComp={Icon.Download}
                          label="Export result"
                          description="Download processed Helix output"
                          onClick={handleHelixExport}
                          loading={loading.helixExport || helixStatus === 'loading'}
                        />
                      </div>

                      <div style={{ background: '#f1f5f9', width: 1, alignSelf: 'stretch' }} />

                      <div style={{ width: 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 32 }}>
                        <div style={{ width: '100%' }}>
                          <p className="boa-last-update-label" style={{ textAlign: 'center', marginBottom: (helixInfo && (helixInfo.insertUser || helixInfo.insertDate || helixInfo.pending > 0)) ? 10 : 0 }}>Getting Lock Unlock</p>
                          {helixInfo && (helixInfo.insertUser || helixInfo.insertDate || helixInfo.pending) && (
                            <div>
                              {helixInfo.insertUser && (
                                <div className="boa-last-update-row">
                                  <span className="boa-last-update-row-icon"><Icon.User /></span>
                                  <span className="boa-last-update-row-text">{helixInfo.insertUser}</span>
                                </div>
                              )}
                              {helixInfo.insertDate && (
                                <div className="boa-last-update-row">
                                  <span className="boa-last-update-row-icon"><Icon.Time /></span>
                                  <span className="boa-last-update-row-text">{formatTimestamp(helixInfo.insertDate)}</span>
                                </div>
                              )}
                              {helixInfo.pending > 0 && (
                                <div className="boa-last-update-row">
                                  <span className="boa-last-update-row-icon"><Icon.Hash /></span>
                                  <span className="boa-last-update-row-text">Pending: {helixInfo.pending}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <HelixStatus status={helixStatus} />
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* ── BOTTOM ROW: Helix Queue full width ───────────────────── */}
              <div className="boa-card">
                <div className="boa-card-header">
                  <span className="boa-card-title">Helix Queue</span>
                  <span className="boa-badge" style={{ background: '#faf5ff', color: '#7c3aed' }}>Queue</span>
                </div>
                <div className="boa-card-body" style={{ padding: 0 }}>
                  <div className="boa-table-wrapper">
                    <table className="boa-table">
                      <thead>
                        <tr>
                          <th>Insert Date</th>
                          <th>Insert User</th>
                          <th>CRQ Change ID</th>
                          <th>Type</th>
                          <th>Error</th>
                          <th>Message</th>
                          <th>RPA Start</th>
                          <th>RPA End</th>
                          <th>Bot</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {helixQueue.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="boa-table-empty">No data available</td>
                          </tr>
                        ) : helixQueue.map((row) => (
                          <tr key={row.crqChangeId}>
                            <td>{formatTimestamp(row.insertDate)}</td>
                            <td>{row.insertUser}</td>
                            <td>{row.crqChangeId}</td>
                            <td>{row.type}</td>
                            <td>{row.error === true ? <span className="boa-table-error">✕ Error</span> : ''}</td>
                            <td className="boa-table-msg boa-table-msg-clickable" onClick={() => row.message && setSelectedMessage(row.message)}>{row.message}</td>
                            <td>{formatTimestamp(row.rpaStart)}</td>
                            <td>{formatTimestamp(row.rpaEnd)}</td>
                            <td>{row.bot}</td>
                            <td>
                              {row.status === 'ONGOING' && <span className="boa-status-spinning" title="Ongoing" />}
                              {row.status === 'DONE' && (
                                <svg className="boa-status-done" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                              )}
                              {row.status === 'ERROR' && (
                                <svg className="boa-status-error" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>
    </Page>
  );
}

export default BackOnAir;